/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  FocusEvent,
  memo,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import {Account, GenericObject} from '@ion/core/src/types/entities';
import {isValue} from '@ion/core/src/utils/StringUtil';
import SSOApps, {Entry} from './SSOApps';
import {setSSODomain, SSOFormFields, TargetType} from './SSOEditorUtil';
import SSOInfo from './SSOInfo';

type AccountSSOConfigProps = {
  selectedAccount?: Account;
  formState?: GenericObject;
  setFormState?: Function;
  ssoAppInfo?: Entry[];
  setSSOAppInfo?: Function;
  isEditSSOMode?: boolean;
  allowEdit?: boolean;
  isSSOConfigDeleted?: boolean;
}
export const AccountSSOConfig = ({
  selectedAccount,
  formState,
  setFormState,
  ssoAppInfo,
  setSSOAppInfo,
  allowEdit = false,
  isSSOConfigDeleted = false,
  isEditSSOMode: isEditMode
}: AccountSSOConfigProps) => {
  const dialogTitle = useMemo(() => {
    const entry = isEditMode ? 'edit' : 'add';
    return translate(`common:editors.shared.${entry}`, {
      entityLabel : translate('admin-groups:ssoConfig.title')
    });
  }, [isEditMode]);

  /**
   * This method is use to update the `ssoAppInfo` for deleted
   * `SSO Configuration` ...
   */
  const updateSSOAppInfo = useCallback(function(ssoApps: Entry[]) {
    ssoApps?.forEach(item => {
      item.apps[SSOFormFields.ClientId] = null;
      item.apps[SSOFormFields.ClientSecret] = null;
      item.apps[SSOFormFields.RedirectUri] = null;
      if (item.apps?.[SSOFormFields.Scope]) {
        delete item.apps[SSOFormFields.Scope];
      }
    });
    return ssoApps;
  }, [isSSOConfigDeleted, ssoAppInfo]);

  /**
   * Setting the `formState` for deleted SSO Configuration. 
   */
  useEffect(() => {
    if (isSSOConfigDeleted) {
      setFormState({
        [SSOFormFields.Domains]           : {required: false},
        [SSOFormFields.DiscoveryEndpoint] : {required: false},
        ssoConfig                         : {name: 'ssoConfig', value: null},
        ssoMode                           : {name: 'ssoMode', value: null}
      });
      updateSSOAppInfo(ssoAppInfo || []);
    }
  }, [isSSOConfigDeleted]);

  const debounceFieldChange = useCallback(debounce((target: TargetType) => {
    const {name, value, required = false} = target;
    setFormState({[name]: {name, value, required}});
  }, 400), []);

  const handleFieldChange = useCallback(function(e: ChangeEvent<HTMLInputElement>) {
    e.persist?.();
    debounceFieldChange(e.target);
  }, []);
  
  const checkSSODomain = useCallback(debounce(({value = null}) => (
    setSSODomain(value, setFormState)
  ), 400), [formState[SSOFormFields.Domains]]);

  const handleBlur = useCallback((e: FocusEvent<HTMLInputElement>) => {
    e.persist?.();
    checkSSODomain(e.target);
  }, [formState]);

  const handleChooserChange = useCallback(({name, value, required = false}) => {
    setFormState({[name]: {name, value: isValue(value, 'none') ? null : value, required}});
  }, []);

  const handleSSOConfigApps = function(value) {
    setSSOAppInfo(value);
    handleChooserChange({
      name  : SSOFormFields.APPs,
      value : value
    });
  };
  
  return (
    <>
      <SSOInfo handleChooserChange={handleChooserChange}
        dialogTitle={dialogTitle}
        handleFieldChange={handleFieldChange}
        onBlur={handleBlur}
        disabled={!allowEdit}
        selectedAccount={selectedAccount}
        formState={formState} />
      <SSOApps ssoAppInfo={ssoAppInfo}
        onChange={handleSSOConfigApps}
        disabled={!allowEdit}
        minApp={1} />
    </>
  );
};

export default memo(AccountSSOConfig);
